import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { formatDate } from "../../utils/Utilitats";

const PAGE_SIZES = [10, 25, 50];

function idEmpleat(e) {
  return e ? Number(e.idEmpleats) : null;
}

function nomComplet(empleat) {
  return [
    [empleat.Cognom1, empleat.Cognom2].filter((x) => x && String(x).trim()).join(" "),
    empleat.Nom,
  ]
    .filter((x) => x && String(x).trim())
    .join(", ");
}

function SeleccionarEmpleat({
  onEmpleatSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  onGestio,
  selectedEmpleat,
  filterText,
  setFilterText,
  setEmpleatsLlista,
  reloadToken,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [empleats, setEmpleats] = useState([]);
  const [filteredEmpleats, setFilteredEmpleats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [mostrarActius, setMostrarActius] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchEmpleats = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Empleats.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa, mostrarActius }),
        });
        const result = await response.json();
        setEmpleats(result.success ? result.empleats || [] : []);
      } catch (err) {
        console.error("Error carregant empleats:", err);
        setEmpleats([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEmpleats();
  }, [empresaSeleccionada, mostrarActius, reloadToken]);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const t = (filterText || "").toLowerCase();
    const filtered = (empleats || []).filter((empleat) => {
      const camp = [
        empleat.Nom,
        empleat.Cognom1,
        empleat.Cognom2,
        empleat.CIF_DNI,
        empleat.TelefonMobil,
        empleat.Categoria,
        empleat.Professio,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !t || camp.includes(t);
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue;
        let bValue;
        if (sortConfig.key === "Nom") {
          aValue = nomComplet(a).toLowerCase();
          bValue = nomComplet(b).toLowerCase();
        } else {
          aValue = (a[sortConfig.key] || "").toString().toLowerCase();
          bValue = (b[sortConfig.key] || "").toString().toLowerCase();
        }
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredEmpleats(filtered);
    setEmpleatsLlista?.(filtered);
    setPage(1);
  }, [filterText, empleats, sortConfig, setEmpleatsLlista]);

  useEffect(() => {
    if (!selectedEmpleat) return;
    const stillVisible = filteredEmpleats.some((e) => idEmpleat(e) === idEmpleat(selectedEmpleat));
    if (!stillVisible) onEmpleatSelect?.(null);
  }, [filteredEmpleats, selectedEmpleat, onEmpleatSelect]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, empleat) => {
    e.stopPropagation();
    const id = idEmpleat(empleat);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = 240;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filteredEmpleats.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredEmpleats.slice(inici, inici + pageSize);
  const empleatMenu = menuObert ? filteredEmpleats.find((e) => idEmpleat(e) === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (empleat) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idEmpleat(empleat) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, empleat)}
      >
        …
      </button>
    </div>
  );

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const tancarI = (fn) => {
    setMenuObert(null);
    fn?.();
  };

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per nom, DNI, telèfon…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Mostra actius</span>
          <span className="dv2-toggle">
            <input type="checkbox" checked={mostrarActius} onChange={() => setMostrarActius((v) => !v)} />
            <span className="dv2-toggle-track" />
          </span>
        </label>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant empleats…</p>
      ) : filteredEmpleats.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap empleat.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((empleat) => (
                <article
                  key={idEmpleat(empleat)}
                  className="dv2-record"
                  onClick={() => onEmpleatSelect?.(empleat)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(empleat)}
                >
                  <div>
                    <p className="dv2-record-title">{nomComplet(empleat) || "—"}</p>
                    <p className="dv2-record-meta">
                      {[empleat.Categoria, empleat.TelefonMobil, formatDate(empleat.DataAlta)]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {renderAccions(empleat)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("DataAlta", "Data alta")}
                    {!mostrarActius && th("DataBaixa", "Data baixa")}
                    {th("CIF_DNI", "DNI")}
                    {th("Nom", "Nom")}
                    {th("TelefonMobil", "Telèfon")}
                    {th("Categoria", "Categoria")}
                    {th("Professio", "Activitat")}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((empleat) => (
                    <tr
                      key={idEmpleat(empleat)}
                      className={idEmpleat(selectedEmpleat) === idEmpleat(empleat) ? "is-selected" : ""}
                      onClick={() => onEmpleatSelect?.(empleat)}
                      onDoubleClick={() => pucConsultar && onConsultar?.(empleat)}
                    >
                      <td>{formatDate(empleat.DataAlta) || "—"}</td>
                      {!mostrarActius && <td>{formatDate(empleat.DataBaixa) || "—"}</td>}
                      <td>{empleat.CIF_DNI || "—"}</td>
                      <td>{nomComplet(empleat) || "—"}</td>
                      <td>{empleat.TelefonMobil || "—"}</td>
                      <td>{empleat.Categoria || "—"}</td>
                      <td>{empleat.Professio || "—"}</td>
                      <td className="dv2-table-actions">{renderAccions(empleat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {empleatMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onConsultar?.(empleatMenu))}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onModificar?.(empleatMenu))}>
                Modificar
              </button>
            )}
            {onGestio && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onGestio?.(empleatMenu))}>
                Gestió
              </button>
            )}
            {pucEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => tancarI(() => onEsborrar?.(empleatMenu))}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SeleccionarEmpleat;
